import { useState, useEffect, useCallback, type ReactNode } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { useHaptics } from "@/hooks/use-haptics";
import { useSwipeTabs } from "@/hooks/use-swipe-tabs";
import SwipeAnimatedContent from "@/components/SwipeAnimatedContent";
import TabDots from "@/components/TabDots";

export interface ToolDef {
  value: string;
  label: string;
}

export interface ToolCategory {
  id: string;
  label: string;
  color: string;
  tools: ToolDef[];
}

interface TradeToolShellProps {
  title: string;
  subtitle: string;
  categories: ToolCategory[];
  /** Maps legacy `?tool=` values to their current value. */
  aliases?: Record<string, string>;
  /** Renders the active tool. `goTo` jumps to any other tool across categories. */
  renderTool: (tool: string, goTo: (tool: string) => void) => ReactNode;
}

/**
 * Shared category → tool → content shell used by every trade page.
 * Handles deep-linking (`?tool=`), swipe navigation, category boundaries and tab dots
 * so individual trade pages only declare their tool map and renderer.
 */
const TradeToolShell = ({ title, subtitle, categories, aliases = {}, renderTool }: TradeToolShellProps) => {
  const { lightTap } = useHaptics();
  const [searchParams] = useSearchParams();

  const resolve = useCallback((value: string) => aliases[value] ?? value, [aliases]);
  const findCategory = useCallback(
    (toolValue: string) => categories.find(c => c.tools.some(t => t.value === toolValue)),
    [categories],
  );

  const requestedTool = searchParams.get("tool");
  const resolvedTool = requestedTool ? resolve(requestedTool) : null;
  const initialCat = resolvedTool ? findCategory(resolvedTool) : null;

  const [category, setCategory] = useState(initialCat?.id ?? categories[0].id);
  const [activeTool, setActiveTool] = useState(
    initialCat ? resolvedTool! : categories[0].tools[0].value,
  );
  const currentCategory = categories.find(c => c.id === category) ?? categories[0];

  useEffect(() => {
    if (!resolvedTool) return;
    const cat = findCategory(resolvedTool);
    if (cat) {
      setCategory(cat.id);
      setActiveTool(resolvedTool);
    }
  }, [resolvedTool, findCategory]);

  const goTo = useCallback(
    (tool: string) => {
      const target = resolve(tool);
      const cat = findCategory(target);
      if (!cat) return;
      setCategory(cat.id);
      setActiveTool(target);
    },
    [resolve, findCategory],
  );

  const handleCategoryChange = (catId: string) => {
    lightTap();
    const cat = categories.find(c => c.id === catId)!;
    setCategory(catId);
    setActiveTool(cat.tools[0].value);
  };

  const handleToolChange = useCallback((tool: string) => {
    lightTap();
    setActiveTool(tool);
  }, [lightTap]);

  const toolValues = currentCategory.tools.map(t => t.value);

  const handleBoundarySwipe = useCallback((dir: "left" | "right") => {
    const catIdx = categories.findIndex(c => c.id === category);
    if (dir === "left" && catIdx < categories.length - 1) {
      const next = categories[catIdx + 1];
      lightTap(); setCategory(next.id); setActiveTool(next.tools[0].value);
      toast(next.label, { duration: 1200 });
    } else if (dir === "right" && catIdx > 0) {
      const prev = categories[catIdx - 1];
      lightTap(); setCategory(prev.id); setActiveTool(prev.tools[prev.tools.length - 1].value);
      toast(prev.label, { duration: 1200 });
    }
  }, [categories, category, lightTap]);

  const swipeHandlers = useSwipeTabs({
    tabs: toolValues,
    activeTab: activeTool,
    onChangeTab: handleToolChange,
    onBoundarySwipe: handleBoundarySwipe,
  });

  const manyTools = currentCategory.tools.length > 6;

  return (
    <div className="pb-24 animate-fade-in">
      <PageHeader title={title} subtitle={subtitle} />
      <div className="px-4 mt-2">
        <div className="flex gap-1.5 bg-secondary rounded-lg p-1" role="tablist" aria-label="Tool categories">
          {categories.map(cat => (
            <button
              key={cat.id}
              role="tab"
              aria-selected={category === cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`flex-1 py-2 px-2 rounded-md text-xs font-semibold transition-all ${
                category === cat.id ? `${cat.color} text-primary-foreground shadow-sm` : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 mt-2 bg-secondary/50 rounded-lg p-1 overflow-x-auto no-scrollbar" role="tablist" aria-label="Tools">
          {currentCategory.tools.map(tool => (
            <button
              key={tool.value}
              role="tab"
              aria-selected={activeTool === tool.value}
              onClick={() => swipeHandlers.changeTab(tool.value)}
              className={`py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                manyTools ? "shrink-0 px-3" : "flex-1 px-2"
              } ${
                activeTool === tool.value ? "bg-card text-foreground shadow-sm border border-border" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tool.label}
            </button>
          ))}
        </div>
      </div>
      <div
        className="px-4 mt-3"
        onTouchStart={swipeHandlers.onTouchStart}
        onTouchMove={swipeHandlers.onTouchMove}
        onTouchEnd={swipeHandlers.onTouchEnd}
      >
        <SwipeAnimatedContent
          activeKey={activeTool}
          swipeDirection={swipeHandlers.swipeDirection}
          edgeBounce={swipeHandlers.edgeBounce}
          onAnimationDone={swipeHandlers.clearDirection}
          onEdgeBounceDone={swipeHandlers.clearEdgeBounce}
        >
          {renderTool(activeTool, goTo)}
        </SwipeAnimatedContent>
        <TabDots
          total={toolValues.length}
          activeIndex={toolValues.indexOf(activeTool)}
          onDotClick={(i) => swipeHandlers.changeTab(toolValues[i])}
        />
      </div>
    </div>
  );
};

export default TradeToolShell;
